import { AnthropicMessagesLanguageModel } from "@ai-sdk/anthropic/internal";
import {
  type LanguageModelV1,
  NoSuchModelError,
  type ProviderV1,
} from "@ai-sdk/provider";
import type {
  AnthropicMessagesModelId,
  AnthropicMessagesSettings,
} from "@ai-sdk/anthropic/internal";
import type { CrosshatchSettings } from "../crosshatch-provider";
import type { FetchFunction } from "@ai-sdk/provider-utils";

export interface AnthropicProvider extends ProviderV1 {
  /**
  Creates a model for text generation.
  */
  (
    modelId: AnthropicMessagesModelId,
    settings?: AnthropicMessagesSettings,
  ): LanguageModelV1;

  /**
  Creates a model for text generation.
  */
  languageModel(
    modelId: AnthropicMessagesModelId,
    settings?: AnthropicMessagesSettings,
  ): LanguageModelV1;

  /**
  @deprecated Use `.languageModel()` instead.
  */
  chat(
    modelId: AnthropicMessagesModelId,
    settings?: AnthropicMessagesSettings,
  ): LanguageModelV1;

  /**
  @deprecated Use `.languageModel()` instead.
     */
  messages(
    modelId: AnthropicMessagesModelId,
    settings?: AnthropicMessagesSettings,
  ): LanguageModelV1;
}

export interface AnthropicProviderSettings extends CrosshatchSettings {
  /**
   * API key for authenticating requests.
   */
  token?: string | (() => string);

  /**
   * Custom headers to include in the requests.
   */
  headers?: Record<string, string>;

  /**
   * Custom fetch implementation. You can use it as a middleware to intercept requests,
   * or to provide a custom fetch implementation for e.g. testing.
   */
  fetch?: FetchFunction;

  generateId?: () => string;
}

/**
  Create an Anthropic provider instance.
   */
export function createCrosshatchAnthropic({
  token,
  baseUrl = "https://ai.crosshatch.dev",
  fetch: fetchFn = fetch,
  ...options
}: AnthropicProviderSettings = {}): AnthropicProvider {
  const createFetch =
    (provider: string, replace: object) =>
    (input: string | URL | Request, init?: RequestInit) => {
      const { headers, url } =
        input instanceof Request
          ? {
              headers: new Headers(input.headers),
              url: new URL(input.url),
            }
          : {
              headers: new Headers(init?.headers),
              url: new URL(input),
            };
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("Crosshatch-Replace", JSON.stringify(replace));
      headers.set("Crosshatch-Provider", provider);
      headers.set("Accept-Encoding", "identity");
      if (input instanceof Request) {
        return input
          .clone()
          .json()
          .then((body) => {
            body.model = undefined;
            body.anthropic_version = "vertex-2023-10-16";
            return fetchFn(new Request(url, { ...input, body, headers }));
          });
      } else {
        const body = JSON.stringify({
          ...JSON.parse(init?.body as string),
          model: undefined,
          anthropic_version: "vertex-2023-10-16",
        });
        return fetchFn(url, { ...init, body, headers });
      }
    };

  const createChatModel = (
    modelId: AnthropicMessagesModelId,
    {
      replace,
      ...settings
    }: AnthropicMessagesSettings & CrosshatchSettings = {},
  ) =>
    new AnthropicMessagesLanguageModel(modelId, settings, {
      provider: "anthropic.messages",
      baseURL: baseUrl,
      fetch: createFetch(modelId, replace || {}),
      headers: options.headers ?? {},
    });

  const provider = function (
    modelId: AnthropicMessagesModelId,
    settings?: AnthropicMessagesSettings,
  ) {
    if (new.target) {
      throw new Error(
        "The Anthropic model function cannot be called with the new keyword.",
      );
    }

    return createChatModel(modelId, {
      ...options,
      ...settings,
      token: typeof token === "function" ? token() : token,
    });
  };

  provider.languageModel = createChatModel;
  provider.chat = createChatModel;
  provider.messages = createChatModel;
  provider.textEmbeddingModel = (modelId: string) => {
    throw new NoSuchModelError({ modelId, modelType: "textEmbeddingModel" });
  };

  return provider;
}

/**
  Default Anthropic provider instance.
   */
export const anthropic = createCrosshatchAnthropic();
